import {
  Activity,
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Globe2,
  LayoutDashboard,
  MousePointer2,
  Package,
  Play,
  RefreshCcw,
  Save,
  ShoppingBag,
  Sparkles,
  Store,
  TrendingUp,
  WandSparkles,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const STEP_TIMINGS = [500, 2450, 4450, 6550, 8750];
const LAST_STEP = 4;

const demoSteps = [
  {
    label: "Ringkasan",
    title: "Semua aktivitas usaha terlihat jelas.",
    text: "Penjualan, pesanan, produk, dan pengunjung dirangkum dalam satu tampilan yang mudah dibaca.",
    icon: LayoutDashboard,
  },
  {
    label: "Pesanan",
    title: "Pesanan baru langsung masuk.",
    text: "Notifikasi muncul, baris pesanan ditambahkan, lalu statusnya bisa diproses tanpa berpindah aplikasi.",
    icon: ShoppingBag,
  },
  {
    label: "Produk",
    title: "Harga dan stok diperbarui seketika.",
    text: "Pilih produk, ubah informasi penting, simpan, lalu data website ikut tersinkron.",
    icon: Package,
  },
  {
    label: "Builder",
    title: "Tampilan website bisa disusun sendiri.",
    text: "Pilih section, lihat hasilnya langsung, lalu publikasikan perubahan ketika sudah sesuai.",
    icon: WandSparkles,
  },
  {
    label: "Analitik",
    title: "Performa bisnis lebih mudah dipahami.",
    text: "Grafik, produk terlaris, dan sumber kunjungan membantu menentukan langkah berikutnya.",
    icon: BarChart3,
  },
];

const sidebarItems = [
  { label: "Ringkasan", icon: LayoutDashboard },
  { label: "Pesanan", icon: ShoppingBag },
  { label: "Produk", icon: Package },
  { label: "Website", icon: Store },
  { label: "Analitik", icon: BarChart3 },
];

function StatTile({ label, value, note, delay = 0 }: { label: string; value: string; note: string; delay?: number }) {
  return (
    <div className="dashboard-demo-stat" style={{ "--demo-delay": `${delay}ms` } as CSSProperties}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small><TrendingUp size={9} /> {note}</small>
    </div>
  );
}

function MiniLineChart({ lively = false }: { lively?: boolean }) {
  return (
    <svg viewBox="0 0 480 170" className={`dashboard-demo-chart ${lively ? "is-lively" : ""}`} role="img" aria-label="Grafik penjualan yang meningkat">
      <defs>
        <linearGradient id="dashboardLineFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#16a34a" stopOpacity=".28" />
          <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[28, 66, 104, 142].map((y) => <line key={y} x1="0" x2="480" y1={y} y2={y} stroke="#e2e8f0" strokeWidth="1" />)}
      <path className="dashboard-demo-chart-area" d="M0 139 C48 130 72 102 112 109 C153 116 170 67 216 77 C260 87 282 48 322 55 C363 62 390 23 430 34 C449 39 466 24 480 17 L480 170 L0 170 Z" fill="url(#dashboardLineFill)" />
      <path className="dashboard-demo-chart-line" d="M0 139 C48 130 72 102 112 109 C153 116 170 67 216 77 C260 87 282 48 322 55 C363 62 390 23 430 34 C449 39 466 24 480 17" fill="none" stroke="#0d944d" strokeLinecap="round" strokeWidth="6" />
      {[0, 112, 216, 322, 430, 480].map((x, index) => {
        const ys = [139, 109, 77, 55, 34, 17];
        return <circle key={x} cx={x} cy={ys[index]} r="5" fill="#fff" stroke="#0d944d" strokeWidth="4" />;
      })}
    </svg>
  );
}

function OverviewScreen() {
  return (
    <div className="dashboard-demo-view dashboard-demo-view--overview">
      <div className="dashboard-demo-view-heading">
        <div><small>Selamat datang kembali,</small><h4>Bumi Sehat Store 👋</h4></div>
        <span>15–21 Mei 2026</span>
      </div>
      <div className="dashboard-demo-stats-grid">
        <StatTile label="Total penjualan" value="Rp12,45 jt" note="18% minggu ini" />
        <StatTile label="Pesanan" value="128" note="12 pesanan baru" delay={80} />
        <StatTile label="Produk" value="56" note="4 stok menipis" delay={160} />
        <StatTile label="Pengunjung" value="1.247" note="22% meningkat" delay={240} />
      </div>
      <div className="dashboard-demo-overview-grid">
        <article className="dashboard-demo-panel dashboard-demo-panel--chart">
          <div className="dashboard-demo-panel-head"><div><small>Ringkasan penjualan</small><strong>Rp12.450.000</strong></div><span>7 hari</span></div>
          <MiniLineChart lively />
        </article>
        <article className="dashboard-demo-panel">
          <div className="dashboard-demo-panel-head"><div><small>Pesanan terbaru</small><strong>Aktivitas hari ini</strong></div><ShoppingBag size={16} /></div>
          <div className="dashboard-demo-order-mini"><span>INV-240521-001</span><b>Rp156.000</b><i>Baru</i></div>
          <div className="dashboard-demo-order-mini"><span>INV-240521-002</span><b>Rp89.000</b><i className="is-process">Proses</i></div>
          <div className="dashboard-demo-order-mini"><span>INV-240521-003</span><b>Rp235.000</b><i className="is-done">Selesai</i></div>
        </article>
      </div>
    </div>
  );
}

function OrdersScreen() {
  const rows = [
    ["INV-240521-005", "Nadia Putri", "Sambal Rendang × 2", "Rp176.000", "Baru"],
    ["INV-240521-004", "Rina Wulandari", "Keripik Tempe × 3", "Rp84.000", "Proses"],
    ["INV-240521-003", "Budi Santoso", "Bumbu Soto × 1", "Rp48.000", "Dikirim"],
    ["INV-240521-002", "Siti Aminah", "Sambal Teri × 2", "Rp120.000", "Selesai"],
  ];

  return (
    <div className="dashboard-demo-view">
      <div className="dashboard-demo-view-heading"><div><small>Transaksi</small><h4>Pesanan</h4></div><button type="button">+ Pesanan manual</button></div>
      <div className="dashboard-demo-order-summary">
        <StatTile label="Pesanan baru" value="13" note="1 baru saja masuk" />
        <StatTile label="Sedang diproses" value="8" note="siap dikemas" delay={100} />
        <StatTile label="Selesai" value="107" note="bulan ini" delay={200} />
      </div>
      <article className="dashboard-demo-table-card">
        <div className="dashboard-demo-table-toolbar"><strong>Pesanan terbaru</strong><span>Cari nomor atau pelanggan…</span></div>
        <div className="dashboard-demo-table-head"><span>No. pesanan</span><span>Pelanggan</span><span>Produk</span><span>Total</span><span>Status</span></div>
        {rows.map((row, index) => (
          <div key={row[0]} className={`dashboard-demo-table-row ${index === 0 ? "is-new" : ""}`}>
            {row.slice(0, 4).map((cell) => <span key={cell}>{cell}</span>)}
            <span><i className={`dashboard-demo-status dashboard-demo-status--${row[4].toLowerCase()}`}>{row[4]}</i></span>
          </div>
        ))}
      </article>
    </div>
  );
}

function ProductsScreen() {
  const products = [
    { name: "Bumbu Rendang Original", price: "Rp28.000", stock: 48, tone: "#92400e" },
    { name: "Sambal Teri Pedas", price: "Rp32.000", oldPrice: "Rp28.000", stock: 31, tone: "#b91c1c" },
    { name: "Keripik Tempe Original", price: "Rp18.000", stock: 64, tone: "#a16207" },
  ];

  return (
    <div className="dashboard-demo-view">
      <div className="dashboard-demo-view-heading"><div><small>Katalog</small><h4>Kelola produk</h4></div><button type="button">+ Tambah produk</button></div>
      <div className="dashboard-demo-products-layout">
        <article className="dashboard-demo-product-list">
          <div className="dashboard-demo-table-toolbar"><strong>56 produk aktif</strong><span>Cari produk…</span></div>
          {products.map((product, index) => (
            <div key={product.name} className={`dashboard-demo-product-row ${index === 1 ? "is-selected" : ""}`}>
              <span className="dashboard-demo-product-thumb" style={{ background: product.tone }}><Store size={17} /></span>
              <div><strong>{product.name}</strong><small>Stok {product.stock}</small></div>
              <div className="dashboard-demo-product-price">{product.oldPrice && <del>{product.oldPrice}</del>}<b>{product.price}</b></div>
              <ChevronRight size={15} />
            </div>
          ))}
        </article>
        <article className="dashboard-demo-editor">
          <div className="dashboard-demo-editor-cover"><span>Sambal Teri Pedas</span></div>
          <label>Nama produk<input value="Sambal Teri Pedas" readOnly /></label>
          <div className="grid grid-cols-2 gap-2">
            <label>Harga<input value="32.000" readOnly /></label>
            <label>Stok<input value="31" readOnly /></label>
          </div>
          <button type="button"><Save size={13} /> Perubahan tersimpan</button>
        </article>
      </div>
    </div>
  );
}

function BuilderScreen() {
  return (
    <div className="dashboard-demo-view dashboard-demo-view--builder">
      <div className="dashboard-demo-builder-topbar">
        <div><small>Website Builder</small><strong>Bumi Sehat Store</strong></div>
        <div><span>Draft tersimpan</span><button type="button">Publikasikan</button></div>
      </div>
      <div className="dashboard-demo-builder-layout">
        <aside className="dashboard-demo-builder-library">
          <small>Tambah section</small>
          {[
            ["Hero", WandSparkles],
            ["Produk", Package],
            ["Keunggulan", CheckCircle2],
            ["Testimoni", Sparkles],
          ].map(([label, Icon]) => {
            const SectionIcon = Icon as typeof Package;
            return <button key={label as string} type="button" className={label === "Hero" ? "is-active" : ""}><SectionIcon size={14} />{label as string}<span>+</span></button>;
          })}
        </aside>
        <div className="dashboard-demo-builder-canvas">
          <div className="dashboard-demo-site-preview">
            <nav><b>Bumi Sehat</b><span>Beranda &nbsp; Produk &nbsp; Tentang</span><button type="button">Pesan</button></nav>
            <div className="dashboard-demo-site-hero">
              <div><small>BUMBU PILIHAN KELUARGA</small><h5>Rasa rumahan,<br />siap melengkapi meja makan.</h5><p>Dibuat dari rempah pilihan dan resep keluarga.</p><button type="button">Belanja sekarang</button></div>
              <div className="dashboard-demo-site-product"><Store size={34} /><strong>Bumbu Rendang</strong><span>Original</span></div>
            </div>
            <div className="dashboard-demo-site-benefits"><span>✓ Rempah asli</span><span>✓ Tanpa pengawet</span><span>✓ Siap dikirim</span></div>
          </div>
          <div className="dashboard-demo-publish-success"><CheckCircle2 size={15} /> Website berhasil dipublikasikan</div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsScreen() {
  return (
    <div className="dashboard-demo-view">
      <div className="dashboard-demo-view-heading"><div><small>Insight bisnis</small><h4>Analitik</h4></div><span>7 hari terakhir</span></div>
      <div className="dashboard-demo-analytics-grid">
        <article className="dashboard-demo-panel dashboard-demo-panel--chart dashboard-demo-analytics-main">
          <div className="dashboard-demo-panel-head"><div><small>Total penjualan</small><strong>Rp12.450.000</strong></div><span className="is-positive">+18%</span></div>
          <MiniLineChart lively />
          <div className="dashboard-demo-chart-labels"><span>15 Mei</span><span>17 Mei</span><span>19 Mei</span><span>21 Mei</span></div>
        </article>
        <article className="dashboard-demo-panel dashboard-demo-donut-panel">
          <div className="dashboard-demo-panel-head"><div><small>Sumber kunjungan</small><strong>1.247 pengunjung</strong></div><Globe2 size={16} /></div>
          <div className="dashboard-demo-donut"><span>60%<small>Organik</small></span></div>
          <ul><li><i className="bg-brand-600" />Pencarian <b>60%</b></li><li><i className="bg-accent-500" />Media sosial <b>25%</b></li><li><i className="bg-slate-300" />Langsung <b>15%</b></li></ul>
        </article>
      </div>
      <div className="dashboard-demo-insight-strip"><Activity size={17} /><div><strong>Produk Sambal Teri naik 34%</strong><span>Performa terbaik minggu ini. Pertimbangkan menambah stok dan promosi.</span></div><ArrowRight size={16} /></div>
    </div>
  );
}

function DashboardScreen({ step }: { step: number }) {
  const screens: ReactNode[] = [<OverviewScreen />, <OrdersScreen />, <ProductsScreen />, <BuilderScreen />, <AnalyticsScreen />];
  return <div key={step} className="dashboard-demo-screen-enter">{screens[step]}</div>;
}

function getCursorPosition(step: number) {
  const positions = [
    [18, 19],
    [17, 30],
    [18, 42],
    [16, 54],
    [18, 66],
  ];
  return positions[step] ?? positions[0];
}

export function DashboardDemoSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const visibilityRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [runId, setRunId] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (visible && !visibilityRef.current) {
          visibilityRef.current = true;
          setInView(true);
          setRunId((current) => current + 1);
        } else if (!entry.isIntersecting || entry.intersectionRatio < 0.05) {
          visibilityRef.current = false;
          setInView(false);
          setStep(0);
        }
      },
      { threshold: [0, 0.05, 0.2, 0.45, 0.75], rootMargin: "-4% 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setStep(0);
    if (reducedMotion) {
      setStep(LAST_STEP);
      return;
    }
    const timers = STEP_TIMINGS.map((delay, index) => window.setTimeout(() => setStep(index), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [inView, runId]);

  const activeStep = demoSteps[step];
  const cursor = useMemo(() => getCursorPosition(step), [step]);
  const progress = Math.round(((step + 1) / demoSteps.length) * 100);

  const replay = () => {
    setStep(0);
    setRunId((current) => current + 1);
  };

  return (
    <section ref={sectionRef} id="dashboard" className="dashboard-demo-section relative overflow-hidden py-20 sm:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,#fbfcfa_0%,#f1faf4_48%,#fff8f1_100%)]" />
      <div className="soft-grid pointer-events-none absolute inset-0 opacity-40" />
      <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-brand-200/35 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-10 h-96 w-96 rounded-full bg-accent-200/30 blur-3xl" />

      <div className="app-container relative">
        <div className={`scroll-reveal scroll-reveal--up ${inView ? "is-visible" : ""} text-center`}>
          <span className="eyebrow"><Play size={14} fill="currentColor" /> Demo dashboard</span>
          <h2 className="section-title mx-auto mt-5 max-w-4xl">Chat untuk mulai. Dashboard untuk berkembang.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">
            Setelah website jadi, kelola produk, pesanan, tampilan, dan performa bisnis dalam satu pusat kendali.
          </p>
        </div>

        <div className="dashboard-demo-stage mt-12">
          <div className="dashboard-demo-copy">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-black text-brand-700"><Sparkles size={14} /> Simulasi aktivitas bisnis</span>
            <div key={`copy-${runId}-${step}`} className="dashboard-demo-copy-enter">
              <p className="mt-5 text-xs font-black uppercase tracking-[.17em] text-accent-600">{activeStep.label}</p>
              <h3 className="mt-2 max-w-xl text-3xl font-black tracking-[-.045em] text-slate-950 sm:text-4xl">{activeStep.title}</h3>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">{activeStep.text}</p>
            </div>

            <div className="mt-7 space-y-2.5">
              {demoSteps.map(({ label, icon: Icon }, index) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setStep(index)}
                  className={`dashboard-demo-step ${step === index ? "is-active" : ""} ${step > index ? "is-complete" : ""}`}
                >
                  <span>{step > index ? <Check size={14} strokeWidth={3} /> : <Icon size={16} />}</span>
                  <strong>{label}</strong>
                  <i>{index + 1}</i>
                </button>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="button" onClick={replay} className="btn-secondary !rounded-xl"><RefreshCcw size={17} /> Putar ulang demo</button>
              <Link to="/login" target="_blank" rel="noopener noreferrer" className="btn-primary !rounded-xl">Buka dashboard <ExternalLink size={16} /></Link>
            </div>
          </div>

          <div className="dashboard-demo-device-wrap" aria-live="polite">
            <div className="dashboard-demo-laptop" key={`dashboard-${runId}`}>
              <div className="dashboard-demo-laptop-lid">
                <div className="dashboard-demo-camera" />
                <div className="dashboard-demo-app">
                  <aside className="dashboard-demo-sidebar">
                    <div className="dashboard-demo-brand"><img src="/brand/logo-mark-exact.png" alt="" /><b>CSJW</b></div>
                    <nav>
                      {sidebarItems.map(({ label, icon: Icon }, index) => (
                        <span key={label} className={step === index ? "is-active" : ""}><Icon size={13} />{label}{label === "Pesanan" && <i>{step >= 1 ? 13 : 12}</i>}</span>
                      ))}
                    </nav>
                    <div className="dashboard-demo-plan"><small>Paket Anda</small><strong>Profesional</strong><button type="button">Lihat fitur</button></div>
                  </aside>
                  <main className="dashboard-demo-main"><DashboardScreen step={step} /></main>
                  <span
                    className="dashboard-demo-cursor"
                    style={{ "--cursor-x": `${cursor[0]}%`, "--cursor-y": `${cursor[1]}%` } as CSSProperties}
                    aria-hidden="true"
                  ><MousePointer2 size={20} fill="white" /></span>
                  {step === 1 && <div className="dashboard-demo-toast"><span><ShoppingBag size={15} /></span><div><strong>Pesanan baru masuk</strong><small>Nadia Putri · Rp176.000</small></div><CheckCircle2 size={15} /></div>}
                  {step === 2 && <div className="dashboard-demo-toast"><span><Save size={15} /></span><div><strong>Produk diperbarui</strong><small>Harga dan stok sudah tersimpan</small></div><CheckCircle2 size={15} /></div>}
                  {step === 3 && <div className="dashboard-demo-toast"><span><Globe2 size={15} /></span><div><strong>Website sudah online</strong><small>Perubahan berhasil dipublikasikan</small></div><CheckCircle2 size={15} /></div>}
                </div>
              </div>
              <div className="dashboard-demo-laptop-base"><span /></div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-4xl">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[.14em] text-slate-400"><span>{activeStep.label}</span><span>{progress}%</span></div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white shadow-inner"><span className="block h-full rounded-full bg-gradient-to-r from-brand-500 via-brand-600 to-accent-500 transition-[width] duration-700 ease-out" style={{ width: `${progress}%` }} /></div>
        </div>
      </div>
    </section>
  );
}
